import { defineStore } from "pinia";
import { ref } from "vue";
import { User, CookieConsent } from "../models/link";
import { AppConfig } from '../config';
import { useLinkStore } from './links';
import { useListsStore } from './lists';
import { useChannelStore } from './channels';
import Cookies from "js-cookie";
import { useNotificationStore } from './notifications';
import api from "../plugins/api";
import axios from "axios";

export const useUserStore = defineStore('user', () => {
  const notificationStore = useNotificationStore();
  const user = ref<User>({} as User);
  const needsSiteLogin = ref(false);
  const needsYoutubeLogin = ref(false);
  const needsGoogleLogin = ref(false);

  async function getGoogleUser(token: string) {
    try {
      const { data: jsonData } = await axios.get(`https://www.googleapis.com/oauth2/v3/tokeninfo?id_token=${token}`);
      
      user.value = {
        id: user.value.id || '',
        imageUrl: jsonData.picture,
        name: jsonData.name,
        token: jsonData,
        tokenId: token
      };
      _saveUserInfoToCookie(jsonData, token);
      needsGoogleLogin.value = false;
    } catch (error: any) {
      notificationStore.showError(`Google login failed: ${error.message}`);
      throw error;
    }
  }

  function _saveUserInfoToCookie(userData: any, token?: string) {
    Cookies.set('user', JSON.stringify({
      name: userData.name, 
      imageUrl: userData.picture, 
      tokenId: token || userData.tokenId 
    }), { expires: 7 });
  }

  function _getUserInfoFromCookie() {
    const userCookie = Cookies.get('user');
    return userCookie ? JSON.parse(userCookie) : {};
  }

  function saveCookieConsent(consentStatus: CookieConsent) {
    Cookies.set('consent', consentStatus, { expires: 365 });
  }

  function getCookieConsent(): CookieConsent {
    const cookieConsent = Cookies.get('consent');
    return (cookieConsent as CookieConsent) || CookieConsent.NotSet;
  }

  async function login(tokenID: string) {
    try {
      // Login is currently on DOMAIN instead of API_DOMAIN
      const response = await api.post(`${AppConfig.DOMAIN}login`, { id: tokenID }, { baseURL: '' });
      
      if (response.status === 206) {
        needsYoutubeLogin.value = true;
      }
      
      const { data } = response;
      user.value.id = data.id;
      user.value.isAdmin = data.role === 'admin';
      user.value.ownedChannels = data.ownedChannels;
      needsSiteLogin.value = false;
    } catch (error: any) {
      if (error.response?.status === 401) {
        needsSiteLogin.value = true;
      } else {
        throw error;
      }
    }
  }

  async function getCurrentUser() {
    try {
      // Check if we have an active session first
      const { data: sessionStatus } = await api.get('/auth/session');
      const userCookie = _getUserInfoFromCookie();

      if (userCookie.tokenId && !user.value.tokenId) {
        user.value.tokenId = userCookie.tokenId;
      }

      if (!sessionStatus.authenticated) {
        if (user.value.tokenId) {
          try {
            await login(user.value.tokenId);
          } catch (loginErr) {
            // Token is likely invalid/expired. Reset credentials and prompt sign in.
            reset();
            return;
          }
        } else {
          needsGoogleLogin.value = true;
          return;
        }
      }

      const { data } = await api.get('/user');
      
      if (userCookie.imageUrl && userCookie.name) {
        needsGoogleLogin.value = false;
      }
      
      user.value.id = data.id || user.value.id;
      user.value.isAdmin = data.role === 'admin';
      user.value.ownedChannels = data.ownedChannels;
      user.value.imageUrl = userCookie.imageUrl || data.imageUrl;
      user.value.name = userCookie.name || data.name;
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 400) {
        reset();
      } else {
        throw error;
      }
    }
  }

  async function logout() {
    if (!user.value.id) return;

    try {
      await api.post(`${AppConfig.DOMAIN}logout`, {}, { baseURL: '' });
      
      if (user.value.token?.sub) {
        google.accounts.id.revoke(user.value.token.sub, done => {
          if (done.error) notificationStore.showError(done.error);
        });
      }
      
      google.accounts.id.disableAutoSelect();
      reset();
    } catch (error: any) {
      if (error.response?.status === 401) {
        needsSiteLogin.value = true;
      }
      throw error;
    }
  }

  function reset() {
    Cookies.remove('user');
    user.value.id = '';
    needsSiteLogin.value = true;
    needsGoogleLogin.value = true;
    user.value.isAdmin = false;
    
    useLinkStore().reset();
    useListsStore().reset();
    useChannelStore().reset();
  }
  
  return { 
    user,
    needsSiteLogin, 
    needsYoutubeLogin, 
    needsGoogleLogin,
    getGoogleUser, 
    getCurrentUser, 
    login, 
    logout, 
    getCookieConsent, 
    saveCookieConsent,
    reset
  };
});